export const CREDIT_CARDS_KEY = "besarawak_credit_cards_v1";
export const CARD_STATEMENTS_KEY = "besarawak_card_statements_v1";
export const GENERATED_CARD_CLAIMS_KEY = "besarawak_generated_card_claims_v1";
export const CREDIT_CARD_DATA_CHANGED_EVENT = "besarawak:credit-card-data-changed";

export const DEFAULT_CARDS = [
  { id: "CARD-001", bank: "Maybank", label: "Corporate Platinum", last4: "4921", owner: "Jacqueline Ping", pic: "Erica Ng", approver: "Jason Tan Chin Foo", statementDay: 25, members: ["Jacqueline Ping", "Erica Ng", "Fam Kuek Chien", "Eldren Aniell"] },
  { id: "CARD-002", bank: "CIMB", label: "Business Card", last4: "8106", owner: "Andrea Kahar", pic: "Azlin Peter", approver: "Jason Tan Chin Foo", statementDay: 18, members: ["Andrea Kahar", "Azlin Peter", "Denny Tan Yee Soon"] },
];

export const DEFAULT_STATEMENTS = [
  { id: "ST-2026-08-001", cardId: "CARD-001", period: "August 2026", statementAmount: 0, fileName: "", status: "Pending Amount", items: [], notifications: ["PIC notified"] },
  { id: "ST-2026-07-001", cardId: "CARD-001", period: "July 2026", statementAmount: 4280, fileName: "Maybank_July_2026.pdf", status: "Pending Filling", items: [{ id: 1, member: "Erica Ng", date: "08/07/2026", description: "Finance workshop registration", amount: 1280 }, { id: 2, member: "Jacqueline Ping", date: "16/07/2026", description: "Corporate accommodation", amount: 3000 }], notifications: ["PIC notified", "Card members notified"] },
  { id: "ST-2026-06-002", cardId: "CARD-002", period: "June 2026", statementAmount: 1860, fileName: "CIMB_June_2026.pdf", status: "Pending Submission", items: [{ id: 1, member: "Andrea Kahar", date: "11/06/2026", description: "Event venue deposit", amount: 1860 }], notifications: ["PIC notified", "Card members notified", "Card owner notified"] },
  { id: "ST-2026-05-001", cardId: "CARD-001", period: "May 2026", statementAmount: 2450, fileName: "Maybank_May_2026.pdf", status: "Closed", claimId: "CL-2026-CC-001", items: [{ id: 1, member: "Fam Kuek Chien", date: "21/05/2026", description: "Approved business travel expense", amount: 2450 }], notifications: ["PIC notified", "Card members notified", "Card owner notified", "Claim created", "Approval completed"] },
];

const normalizeStatement = (statement) => {
  const oldStatusMap = {
    Generated: "Pending Amount",
    "Collecting Items": "Pending Filling",
    "Owner Review": "Pending Submission",
    "Claim Created": "Submitted",
    Draft: Number(statement.statementAmount) > 0
      ? "Pending Filling"
      : "Pending Amount",
    "Awaiting Approval": "Pending Submission",
    Approved: "Closed",
    Reimbursed: "Closed",
  };
  return {
    ...statement,
    status: oldStatusMap[statement.status] || statement.status,
    notifications: statement.notifications || [],
  };
};

const load = (key, fallback) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    if (Array.isArray(value)) return value;
    window.localStorage.setItem(key, JSON.stringify(fallback));
  } catch (error) { /* Prototype storage falls back to seed data. */ }
  return fallback.map((item) => ({ ...item }));
};

export const loadCreditCards = () => {
  const cards = load(CREDIT_CARDS_KEY, DEFAULT_CARDS);
  const next = cards.map((card) =>
    card.id === "CARD-001" && !(card.members || []).includes("Eldren Aniell")
      ? { ...card, members: [...(card.members || []), "Eldren Aniell"] }
      : card,
  );

  if (next.some((card, index) => card !== cards[index])) {
    window.localStorage.setItem(CREDIT_CARDS_KEY, JSON.stringify(next));
  }
  return next;
};
export const loadCardStatements = () => {
  const cards = loadCreditCards();
  const today = new Date();
  const month = today.toLocaleString("en-GB", { month: "long" });
  const period = `${month} ${today.getFullYear()}`;
  const stored = load(CARD_STATEMENTS_KEY, DEFAULT_STATEMENTS).map(normalizeStatement);
  const generated = cards
    .filter(
      (card) =>
        today.getDate() >= Number(card.statementDay) &&
        !stored.some(
          (statement) => statement.cardId === card.id && statement.period === period,
        ),
    )
    .map((card, index) => ({
      id: `ST-${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(stored.length + index + 1).padStart(3, "0")}`,
      cardId: card.id,
      period,
      statementAmount: 0,
      fileName: "",
      status: "Pending Amount",
      items: [],
      notifications: [`PIC notified: ${card.pic}`],
    }));
  const next = [...generated, ...stored];
  if (generated.length) saveCardStatements(next);
  return next;
};
const notifyCreditCardDataChanged = () =>
  window.dispatchEvent(new CustomEvent(CREDIT_CARD_DATA_CHANGED_EVENT));

export const saveCreditCards = (value) => {
  window.localStorage.setItem(CREDIT_CARDS_KEY, JSON.stringify(value));
  notifyCreditCardDataChanged();
};

export const saveCardStatements = (value) => {
  window.localStorage.setItem(CARD_STATEMENTS_KEY, JSON.stringify(value));
  notifyCreditCardDataChanged();
};

export const countIncompleteCardStatementsForUser = (
  userName,
  cards = loadCreditCards(),
  statements = loadCardStatements(),
) => {
  const normalizedUserName = String(userName || "").trim().toLowerCase();
  if (!normalizedUserName) return 0;

  const relatedCardIds = new Set(
    cards
      .filter((card) =>
        [card.owner, card.pic, ...(card.members || [])]
          .some((name) => String(name || "").trim().toLowerCase() === normalizedUserName),
      )
      .map((card) => card.id),
  );

  return new Set(
    statements
      .filter(
        (statement) =>
          statement.status !== "Closed" && relatedCardIds.has(statement.cardId),
      )
      .map((statement) => statement.id),
  ).size;
};

export const saveGeneratedCardClaim = (claim) => {
  const current = load(GENERATED_CARD_CLAIMS_KEY, []);
  const next = [claim, ...current.filter((item) => item.id !== claim.id)];
  window.localStorage.setItem(GENERATED_CARD_CLAIMS_KEY, JSON.stringify(next));
};

export const loadGeneratedCardClaims = () => load(GENERATED_CARD_CLAIMS_KEY, []);
